import { Button, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { PLATFORM_ORDERS_PATH } from './OrdersArriving';

/**
 * Matching a sale from the sale itself.
 *
 * The line in front of you carries the platform's exact spelling of the dish, and until this
 * existed the only way to act on it was to scroll up to the matching table, press "Add a match",
 * pick the platform again, and RETYPE that spelling by hand — which is the one field in the whole
 * flow that has to be character-perfect, because a typo means the name silently never matches and
 * the money stays off the check for ever.
 *
 * So nothing is retyped. Their name, their code and the platform come from the line; the only
 * question asked is the only one a person can answer, which is what it sells as here. The rematch
 * that puts the sale on its check runs on save, because matching a name on its own changes nothing
 * — that was a second button, on a different part of the screen, that had to be remembered.
 */

const LINES_PATH = '/admin/v1/channels/platform-order-lines';
const MAPPINGS_PATH = '/admin/v1/channels/item-mappings';
const REMATCH_PATH = '/admin/v1/channels/platform-order-lines/rematch';

export interface MatchableLine {
  id?: string;
  channelId?: string;
  platformName?: string;
  platformSku?: string | null;
  qty?: number;
  lineTotalMinor?: number;
  currency?: string;
}

interface Mapping {
  id?: string;
  channelId?: string;
  platformName?: string;
  platformSku?: string | null;
  variantId?: string | null;
}

/**
 * The same normalisation the server matches on (`backend/src/domains/channels/matching.ts`).
 *
 * It is only ever used here to decide whether a name already HAS a row — edit that one rather than
 * write a second saying the same thing — so the two drifting apart costs a duplicate row in the
 * matching table rather than a lost sale. Kept identical anyway: a queue with two entries for
 * "Fish tacos" is a queue somebody stops trusting.
 */
const nameKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text size="sm" mt={2}>
        {children}
      </Text>
    </div>
  );
}

export function MatchLineButton({ line }: { line: MatchableLine }) {
  const queryClient = useQueryClient();
  const channels = useEntityIndex('channel');
  const [open, setOpen] = useState(false);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channelId = line.channelId ?? '';
  const platformName = line.platformName ?? '';

  /**
   * Only fetched once the modal is open, and only to answer one question: has this name already got
   * a row in the matching table? A review-queue row put there by hand — or by an earlier order under
   * the same name — has to be POINTED at a size, not duplicated.
   */
  const mappings = useQuery({
    queryKey: listKey(MAPPINGS_PATH),
    queryFn: () => rawApi<Mapping[]>('GET', MAPPINGS_PATH),
    enabled: open,
  });
  const existing = (mappings.data ?? []).find(
    (m) => m.channelId === channelId && nameKey(m.platformName ?? '') === nameKey(platformName),
  );
  /**
   * A failed read must not turn into a wrong write.
   *
   * "No row for this name" and "the matching table could not be reached" arrive here as the same
   * empty answer, and acting on the second one adds a SECOND row for a name that already has one —
   * which is how a queue ends up with two entries for Fish Tacos and nobody trusts it again.
   */
  const cannotTell = mappings.isError && !mappings.data;

  const submit = async () => {
    if (!variantId) {
      setError('Choose what this sells as on your own menu.');
      return;
    }
    setSaving(true);
    try {
      if (existing?.id) {
        await rawApi('PATCH', `${MAPPINGS_PATH}/${existing.id}`, {
          variantId,
          // Their code only where the row has none and the sale carried one. Overwriting a code
          // somebody typed with a null off a line that never had one is a silent deletion.
          ...(!existing.platformSku && line.platformSku ? { platformSku: line.platformSku } : {}),
        });
      } else {
        await rawApi('POST', MAPPINGS_PATH, {
          channelId,
          platformName,
          platformSku: line.platformSku ?? null,
          variantId,
        });
      }
      // Matching the name changes nothing on its own: every sale already made under it is still
      // missing from its check until the lines go back through.
      const result = await rawApi<{ matched: number; remaining: number }>('POST', REMATCH_PATH, {
        channelId,
      });
      notifyDone(
        result.matched > 0 ? 'Put on their checks' : 'Matched',
        result.matched > 0
          ? `${result.matched} ${result.matched === 1 ? 'line' : 'lines'} sold as “${platformName}” now ring up. ${
              result.remaining === 0
                ? 'Nothing is left waiting.'
                : `${result.remaining} still waiting under other names.`
            }`
          : `“${platformName}” is matched. Nothing was waiting to go on a check.`,
      );
      setOpen(false);
      setVariantId(null);
      for (const path of [LINES_PATH, PLATFORM_ORDERS_PATH, MAPPINGS_PATH]) {
        void queryClient.invalidateQueries({ queryKey: listKey(path) });
      }
    } catch (err) {
      notifyError(err, 'match this to your menu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpen(true)}>
        Match this
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title={`What is “${platformName}” on your menu?`}
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          {/* Everything the platform already told us, read-only, because it is theirs and because
              retyping it is exactly what this screen exists to stop. */}
          <SimpleGrid cols={2} spacing="md">
            <Fact label="Platform">{channels.label(channelId)}</Fact>
            <Fact label="Their name for it">{platformName}</Fact>
            {line.platformSku && <Fact label="Their code">{line.platformSku}</Fact>}
            {/* "2 × $ 17.00" was the multiplier written in front of the LINE TOTAL, which reads as
                thirty-four dollars — the one number in this dialog the manager has any reason to
                check against the platform's own screen. The total is what the sale came to; the
                count goes after it, the way a pack price is written everywhere else here. */}
            <Fact label="This sale">
              {formatMoney(line.lineTotalMinor, line.currency ?? 'HNL')}
              {line.qty && line.qty > 1 ? ` for ${line.qty}` : ''}
            </Fact>
          </SimpleGrid>

          <EntitySelect
            entity="variant"
            label="Rings up as"
            description="The size on your own menu this sells as, so stock and takings are right"
            required
            value={variantId}
            onChange={(v) => {
              setVariantId(v);
              setError(null);
            }}
            error={error}
          />

          {/* Said before the press, not after: this matches the NAME, so every other order that
              already sold under it goes on its check too. That is the point, and it is a surprise
              if the reader thought they were fixing one line. */}
          {cannotTell ? (
            <Text size="xs" c="red">
              The matching table could not be read just now, so this cannot tell whether the name is already
              on it. Nothing has changed. Refresh the page and try again.
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              {existing
                ? 'This name is already in the matching table with nothing against it, so it is pointed at the size you choose rather than added twice.'
                : 'The name is added to the matching table, so their next order under it rings up on its own.'}{' '}
              Everything already sold under this name goes onto its check when you save.
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={saving}
              disabled={mappings.isLoading || cannotTell}
            >
              Match it
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
